(function () {
  "use strict";

  var Drawer = window.Faubourg && window.Faubourg.Drawer;

  if (Drawer && !customElements.get("faubourg-search-sheet")) {
    class SearchSheet extends Drawer {}
    customElements.define("faubourg-search-sheet", SearchSheet);
  }

  var RECENT_KEY = "faubourg:recent-searches";
  var RECENT_MAX = 5;

  var DEBOUNCE = 220;

  var MIN_LENGTH = 2;

  var CACHE_MAX = 20;

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list)
        ? list.filter(function (item) {
            return typeof item === "string" && item.length > 0;
          })
        : [];
    } catch (error) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (error) {}
  }

  class PredictiveSearch extends HTMLElement {
    #controller = null;
    #request = null;
    #timer = null;
    #sequence = 0;
    #cache = new Map();

    connectedCallback() {
      this.input = this.querySelector("[data-search-input]");
      this.form = this.querySelector("form");
      this.resting = this.querySelector("[data-search-resting]");
      this.results = this.querySelector("[data-search-results]");
      this.error = this.querySelector("[data-search-error]");
      this.live = this.querySelector("[data-search-live]");
      this.clear = this.querySelector("[data-search-clear]");

      if (!this.input || !this.form || !this.results) return;

      this.#controller = new AbortController();
      var signal = this.#controller.signal;

      this.addEventListener("input", this.#onInput, { signal: signal });
      this.addEventListener("click", this.#onClick, { signal: signal });
      this.addEventListener("keydown", this.#onKeydown, { signal: signal });
      this.form.addEventListener("submit", this.#onSubmit, { signal: signal });

      this.sheet = this.closest("faubourg-search-sheet");
      if (this.sheet) {
        this.sheet.addEventListener("drawer:open", this.#onOpen, {
          signal: signal,
        });
      }

      this.#renderRecent();
      this.#syncClear();
      this.setAttribute("data-enhanced", "");
    }

    disconnectedCallback() {
      window.clearTimeout(this.#timer);
      if (this.#request) this.#request.abort();
      if (this.#controller) this.#controller.abort();
      this.#controller = null;
    }

    /* --- Interactions --- */

    #onOpen = () => {
      this.#renderRecent();
    };

    #onInput = (event) => {
      if (event.target !== this.input) return;

      this.#syncClear();

      var terms = this.input.value.trim();
      window.clearTimeout(this.#timer);

      if (terms.length < MIN_LENGTH) {
        this.#abandon();
        this.#showResting();
        return;
      }

      var cached = this.#cache.get(terms);
      if (cached) {
        this.#abandon();
        this.#showResults(cached);
        return;
      }

      this.#sequence += 1;
      if (this.#request) this.#request.abort();
      this.#timer = window.setTimeout(() => this.#query(terms), DEBOUNCE);
    };

    #onClick = (event) => {
      var target = event.target;
      if (!target || typeof target.closest !== "function") return;

      if (target.closest("[data-search-clear]")) {
        this.input.value = "";
        this.#syncClear();
        this.#abandon();
        this.#showResting();
        this.input.focus();
        return;
      }

      if (target.closest("[data-search-forget]")) {
        writeRecent([]);
        this.#renderRecent();
        this.input.focus();
        return;
      }

      if (target.closest("[data-search-error-action]")) {
        this.form.submit();
        return;
      }

      // A way out of a search that found nothing is not a search to remember.
      var link = target.closest("a[href]");
      if (link && !link.closest("[data-predictive-empty]")) this.#remember(this.#termOf(link));
    };

    #onKeydown = (event) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

      var stops = this.#stops();
      if (stops.length === 0) return;

      var current = stops.indexOf(document.activeElement);

      if (document.activeElement === this.input) {
        if (event.key === "ArrowUp") return;
        event.preventDefault();
        stops[0].focus();
        return;
      }

      if (current === -1) return;
      event.preventDefault();

      if (event.key === "ArrowUp" && current === 0) {
        this.input.focus();
        return;
      }

      var next = event.key === "ArrowDown" ? current + 1 : current - 1;
      if (next < 0 || next >= stops.length) return;
      stops[next].focus();
    };

    #onSubmit = () => {
      this.#remember();
    };

    #stops() {
      var region = this.results.hasAttribute("hidden")
        ? this.resting
        : this.results;
      if (!region) return [];
      return Array.prototype.filter.call(
        region.querySelectorAll("a[href], button"),
        function (node) {
          return node.offsetParent !== null;
        },
      );
    }

    #termOf(link) {
      try {
        var href = new URL(link.getAttribute("href"), window.location.origin);
        var action = new URL(
          this.form.getAttribute("action"),
          window.location.origin,
        );
        if (href.pathname !== action.pathname) return null;
        return href.searchParams.get("q");
      } catch (error) {
        return null;
      }
    }

    #abandon() {
      this.#sequence += 1;
      if (this.#request) {
        this.#request.abort();
        this.#request = null;
      }
      this.removeAttribute("data-search-busy");
      this.results.removeAttribute("aria-busy");
    }

    #syncClear() {
      if (!this.clear) return;
      if (this.input.value.length > 0) this.clear.removeAttribute("hidden");
      else this.clear.setAttribute("hidden", "");
    }

    /* --- The query --- */

    async #query(terms) {
      this.#sequence += 1;
      var sequence = this.#sequence;

      if (this.#request) this.#request.abort();
      var request = new AbortController();
      this.#request = request;

      this.setAttribute("data-search-busy", "");
      this.results.setAttribute("aria-busy", "true");

      var target = new URL(this.dataset.predictiveUrl, window.location.origin);
      target.searchParams.set("q", terms);
      // What kinds of result come back is the merchant's to set in Search &
      // Discovery, which Shopify honours only while the request names none.
      // The limit is the theme's, and holds for each kind rather than for all
      // of them together.
      target.searchParams.set("resources[limit]", this.dataset.limit || "4");
      target.searchParams.set("resources[limit_scope]", "each");
      target.searchParams.set("section_id", this.dataset.section);

      try {
        var response = await fetch(target.toString(), {
          signal: request.signal,
        });
        if (!response.ok) throw new Error(response.statusText);

        var text = await response.text();

        if (sequence !== this.#sequence) return;

        var markup = window.Faubourg.parseSection(text);
        var fragment = markup.querySelector("[data-predictive-results]");
        if (!fragment) throw new Error("Unrecognised reply");

        // A reply is not a search the shopper made: only a submit or a chosen
        // result is remembered.
        this.#cacheResult(terms, fragment.innerHTML);
        this.#showResults(fragment.innerHTML);
      } catch (error) {
        if (error && error.name === "AbortError") return;
        if (sequence !== this.#sequence) return;
        this.#showError();
      } finally {
        if (sequence === this.#sequence) {
          this.removeAttribute("data-search-busy");
          this.results.removeAttribute("aria-busy");
        }
      }
    }

    #cacheResult(terms, markup) {
      if (this.#cache.size >= CACHE_MAX) {
        this.#cache.delete(this.#cache.keys().next().value);
      }
      this.#cache.set(terms, markup);
    }

    /* --- The states --- */

    #showResting() {
      this.results.innerHTML = "";
      this.results.setAttribute("hidden", "");
      if (this.error) this.error.setAttribute("hidden", "");
      if (this.resting) this.resting.removeAttribute("hidden");
      this.#announce("");
    }

    #showResults(markup) {
      this.results.innerHTML = markup;
      this.results.removeAttribute("hidden");
      if (this.error) this.error.setAttribute("hidden", "");
      if (this.resting) this.resting.setAttribute("hidden", "");

      var status = this.results.querySelector("[data-predictive-status]");
      this.#announce(status ? status.textContent.trim() : "");
    }

    #showError() {
      this.results.innerHTML = "";
      this.results.setAttribute("hidden", "");
      if (this.resting) this.resting.setAttribute("hidden", "");
      if (!this.error) return;

      this.error.removeAttribute("hidden");
      var status = this.error.querySelector("[data-search-error-status]");
      this.#announce(status ? status.textContent.trim() : "");
    }

    #announce(sentence) {
      if (!this.live) return;
      this.live.textContent = sentence;
    }

    /* --- The browser's memory --- */

    #remember(explicit, silent) {
      if (!this.hasAttribute("data-recent")) return;

      var terms = (explicit || this.input.value).trim();
      if (terms.length < MIN_LENGTH) return;

      var list = readRecent().filter(function (item) {
        return item.toLowerCase() !== terms.toLowerCase();
      });

      list.unshift(terms);
      writeRecent(list.slice(0, RECENT_MAX));

      if (!silent) this.#renderRecent();
    }

    #renderRecent() {
      var section = this.querySelector("[data-search-recent]");
      var list = this.querySelector("[data-search-recent-list]");
      if (!section || !list) return;

      var recent = this.hasAttribute("data-recent") ? readRecent() : [];

      if (recent.length === 0) {
        section.setAttribute("hidden", "");
        list.textContent = "";
        return;
      }

      var base = this.form.getAttribute("action");
      list.textContent = "";

      recent.forEach(function (terms) {
        var item = document.createElement("li");
        var link = document.createElement("a");

        link.className = "search-group__link";
        link.href =
          base +
          "?q=" +
          encodeURIComponent(terms) +
          "&options%5Bprefix%5D=last";

        link.textContent = terms;

        item.appendChild(link);
        list.appendChild(item);
      });

      section.removeAttribute("hidden");
    }
  }

  if (!customElements.get("faubourg-predictive-search")) {
    customElements.define("faubourg-predictive-search", PredictiveSearch);
  }
})();
